import { useState, useEffect } from 'react';
import { ReviewService } from '@/lib/reviewService';
import { RatingStats, ReviewTargetType } from '@/types/reviews';

export interface UseRatingStatsResult {
  stats: RatingStats | null;
  isLoading: boolean;
  error: string | null;
  refreshStats: () => Promise<void>;
}

export const useRatingStats = (
  targetType: ReviewTargetType,
  targetId: string
): UseRatingStatsResult => {
  const [stats, setStats] = useState<RatingStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const ratingStats = await ReviewService.getRatingStats(targetType, targetId);
      setStats(ratingStats);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load rating stats');
      // Set default stats on error
      setStats({
        average_rating: 0,
        rating_count: 0,
        rating_distribution: { "1": 0, "2": 0, "3": 0, "4": 0, "5": 0 }
      });
    } finally {
      setIsLoading(false);
    }
  };

  const refreshStats = async () => {
    await fetchStats();
  };

  useEffect(() => {
    if (targetId) {
      fetchStats();
    }
  }, [targetType, targetId]);

  return {
    stats,
    isLoading,
    error,
    refreshStats
  };
}; 